import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  BasicCreatureInstance,
  CustomParticipant,
  Encounter, 
  // EncounterLog, 
  EncounterParticipant,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import InitiativeTrackerParticipant from "@dndComponents/encounter/InitiativeTrackerParticipant";
import CheckBox from "@components/input/CheckBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import FlexBox from '@components/layout/FlexBox';
import StopButton from '@components/input/buttons/StopButton';
import PlayButton from '@components/input/buttons/PlayButton';
import NextButton from '@components/input/buttons/NextButton';

interface Props { 
  obj: Encounter;
  change_time: number;
  onChange: (recalc: boolean) => void;
  onSelect: (selected_participant_id: string, selected_ci_id?: string) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function InitiativeTracker({
  obj,
  change_time,
  onChange,
  onSelect,
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [reloading, setReloading] = useState(false);
  const [selected_participant_id, setSelectedParticipantId] = useState("");
  const [selected_ci_id, setSelectedCIId] = useState("");

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
  }, [change_time, prev_change_time, setChangeTime]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateEncounter = (onFinished: Function | null = null) => {
    onChange(true);
    if (onFinished) {
      onFinished();
    }
  } 

  const nextTurn = (participants: EncounterParticipant[], onFinished: Function | null = null) => {
    obj.current_turn++;
    if (obj.current_turn > Math.max(...(participants.filter(o => o.turn_order !== undefined).map(o => o.turn_order) as number[]))) {
      obj.current_turn = 1;
    }
    updateEncounter(onFinished);
  }
  
  const owner = api.user_id === obj.owner_id || (obj.campaign && obj.campaign.owner_id === api.user_id);
  let participants = owner ? obj.participants : obj.participants.filter(o => !o.hide);
  if (obj.running) {
    participants = participants.sort((a, b) => { 
      if (a.turn_order === undefined || b.turn_order === undefined) {
        if (a.turn_order !== undefined) {
          return -1;
        } else if (b.turn_order !== undefined) {
          return 1;
        } else {
          if (a.include_in_difficulty && !b.include_in_difficulty) {
            return -1;
          } else if (!a.include_in_difficulty && b.include_in_difficulty) {
            return 1;
          } else {
            return 0;
          }
        }
      } else {
        return a.turn_order - b.turn_order;
      }
    });
  }
  return (
    <FlexBox direction="column" style={{ height: "100%" }}>
      { owner &&
        <FlexBox xs_expand={0} xs_shrink={0} direction="column">
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox className="centered">
              <PlayButton 
                disabled={ obj.running || participants.filter(o => o.initiative !== undefined).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  new_obj.running = !new_obj.running;
                  if (new_obj.running) {
                    new_obj.current_turn = 1;
                  }
                  for (const participant of participants) {
                    participant.turn_order = undefined;
                  }
                  let participants_sorted = participants.filter(o => o.initiative !== undefined).sort((a, b) => {
                    if (a.initiative === undefined || b.initiative === undefined) {
                      return 0;
                    } else {
                      return b.initiative - a.initiative;
                    }
                  });
                  if (obj.alternating_order) {
                    // debugger; 
                    const lair_actions = participants_sorted.filter(o => o.participant_type === "Lair Actions");
                    participants_sorted = participants_sorted.filter(o => o.participant_type !== "Lair Actions");
                    const participants_by_color: { [color: string]: { highest_init: number, team: EncounterParticipant[] }} = {};
                    const colors = common_util.unique_array(participants_sorted.map(o => o.color));
                    const highest_inits: number[] = [];
                    for (const color of colors) {
                      const team = participants_sorted.filter(o => o.color === color);
                      const highest_init = team[0].initiative;
                      if (team.length > 0 && highest_init) {
                        participants_by_color[color] = { highest_init, team };
                        highest_inits.push(highest_init);
                      }
                    }
                    let highest_inits_in_order = highest_inits.sort((a, b) => b - a);
                    let teams_in_order: EncounterParticipant[][] = [];
                    while (highest_inits_in_order.length > 0) {
                      const highest_init = highest_inits_in_order[0];
                      const colors_with_it = colors.filter(color => participants_by_color[color].highest_init === highest_init);
                      for (const color of colors_with_it) {
                        teams_in_order.push(participants_by_color[color].team);
                      }
                      highest_inits_in_order = highest_inits_in_order.filter(o => o !== highest_init);
                    }
                    const participants_in_order: EncounterParticipant[] = [];
                    let team_turn = 0;
                    let i = 0; 
                    for (const la of lair_actions) {
                      const next_participant = la;
                      next_participant.turn_order = i + 1;
                      i++;
                      participants_in_order.push(next_participant);
                    }
                    while (participants_in_order.length < (participants_sorted.length + lair_actions.length)) {
                      if (teams_in_order.length === 0) {
                        break;
                      }
                      let team = teams_in_order[team_turn];
                      while (!team || team.length === 0) {
                        team_turn++;
                        if (team_turn >= teams_in_order.length) {
                          team_turn = 0;
                        }
                        team = teams_in_order[team_turn];
                      } 
                      const next_participant = team[0];
                      next_participant.turn_order = i + 1;
                      i++;
                      participants_in_order.push(next_participant);
                      team.splice(0, 1); // = team.filter(o => o.participant_id !== next_participant.participant_id);
                      if (team.length === 0) {
                        teams_in_order = teams_in_order.filter(o => o.length > 0);
                      }
                      team_turn++;
                    }
                  } else {
                    for (let i = 0; i < participants_sorted.length; ++i) {
                      const participant = participants_sorted[i];
                      participant.turn_order = i + 1;
                    }
                  }
                  updateEncounter();
                  setReloading(true);
                }}
              />
            </FlexBox>
            <FlexBox className="centered">
              <StopButton 
                disabled={ !obj.running }
                onClick={() => {
                  const new_obj = obj;
                  new_obj.running = false;
                  new_obj.current_turn = -1;
                  updateEncounter();
                  setReloading(true);
                }}
              />
            </FlexBox>
            <FlexBox className="centered">
              <NextButton
                disabled={ !obj.running }
                onClick={() => {
                  nextTurn(participants);
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox>
              <DandDButtonBox 
                name="Roll Missing"
                disabled={ obj.running || obj.participants.filter(o => o.initiative === undefined && o.include_in_difficulty).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  // debugger;
                  for (const participant of new_obj.participants.filter(o => 
                    o.initiative === undefined && o.include_in_difficulty)) {
                    const adv = participant.initiative_adv;
                    let roll = common_util.get_random(20);
                    if (adv > 0) {
                      roll = Math.max(roll, common_util.get_random(20));
                    } else if (adv < 0) {
                      roll = Math.min(roll, common_util.get_random(20));
                    }
                    // console.log(roll);
                    participant.initiative = Math.max(0, roll + participant.initiative_modifier);
                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="Use Passives"
                disabled={ obj.running || obj.participants.filter(o => o.initiative === undefined && o.include_in_difficulty).length === 0 }
                onClick={() => {
                  for (const participant of obj.participants.filter(o => 
                    o.initiative === undefined && o.include_in_difficulty)) {
                    participant.initiative = 10 + participant.initiative_modifier;
                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox>
              <CheckBox 
                name="Alternating Order"
                value={obj.alternating_order}
                disabled={ obj.running }
                onChange={(changed) => {
                  obj.alternating_order = changed;
                  updateEncounter();
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="Clear Initiatives"
                disabled={ obj.running || obj.participants.filter(o => o.initiative !== undefined && o.participant_type !== "Lair Actions").length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  const the_participants = new_obj.participants.filter(o => o.initiative !== undefined && o.participant_type !== "Lair Actions");
                  for (const part of the_participants) {
                    part.initiative = undefined;
                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      }
      <FlexBox className="padded bordered">
        <FlexBox direction="column" style={{ height: "100%", overflowY: "scroll" }}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <Grid container spacing={0} direction="row">
                { !reloading && participants.map(participant => {
                  return (
                    <Grid size={12} key={participant.participant_id}>
                      <InitiativeTrackerParticipant
                        e_participant={participant}
                        encounter={obj}
                        change_time={change_time}
                        selected_participant_id={selected_participant_id}
                        selected_ci_id={selected_ci_id}
                        onSelect={(ci: BasicCreatureInstance | CustomParticipant | null) => {
                          if (ci) {
                            const new_id = (participant.participant_id === selected_participant_id && ci.true_id === selected_ci_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            setSelectedCIId(new_id === "" ? "" : ci.true_id);
                            onSelect(new_id, new_id === "" ? "" : ci.true_id);
                          } else {
                            const new_id = (participant.participant_id === selected_participant_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            onSelect(new_id);
                          }
                        }}
                        onChange={() => {
                          updateEncounter();
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}
